import { beHttp } from './beHttp';

/**
 * Popup insistente + sub-tab de Perfil de "estilo terapéutico" (preguntas de escenario
 * C-NIP, contenido administrado por quieroserpaciente) — vive en psiplatform-be, no en
 * api-core. Ver psiplatform-be/src/modules/therapeutic-style.
 */

export interface TherapeuticStyleOption {
  id: string;
  letter: string;
  label: string;
  category: string;
}

export interface TherapeuticStyleQuestion {
  id: string;
  order: number;
  titulo: string;
  escenario: string;
  pregunta: string;
  options: TherapeuticStyleOption[];
  /** `type` real en `clinical_preferences` — la key que va en `preferences` al guardar. */
  realFilterKey: string;
  /** categoría de la opción elegida -> valor exacto que espera api-core. */
  categoryFilterValues: Record<string, string>;
}

export interface TherapeuticStylePromptState {
  skipCount: number;
  lastSkippedAt: string | null;
  /** true a partir del 5to skip — el popup deja de ser skippable. */
  mandatory: boolean;
  /** null si no hay cooldown activo. */
  cooldownActiveUntil: string | null;
}

export interface TherapeuticStyleState {
  /** `[]` si el módulo no aplica hoy — no elegible, sin preguntas activas, o error del
   *  lado de quieroserpaciente. El caller SIEMPRE debe tratarlo como "ocultar todo". */
  questions: TherapeuticStyleQuestion[];
  /** `realFilterKey` -> valor exacto (`categoryFilterValues[...]`) ya guardado. Ausente =
   *  todavía sin responder. */
  answers: Record<string, string>;
  promptState: TherapeuticStylePromptState;
}

const BASE_PATH = '/api/psychologist/therapeutic-style';

const EMPTY_PROMPT_STATE: TherapeuticStylePromptState = {
  skipCount: 0,
  lastSkippedAt: null,
  mandatory: false,
  cooldownActiveUntil: null,
};

/**
 * Normaliza la respuesta cruda antes de devolverla — un shape inesperado (un catch-all de
 * test que no conoce este endpoint todavía, un proxy intermedio, un backend viejo) no debe
 * poder tirar abajo Agenda/Perfil: `questions.filter(...)` sobre `undefined` rompe el
 * render entero, y acá es donde se corta esa cadena, no en cada componente que consume esto.
 */
function normalize(raw: Partial<TherapeuticStyleState> | null | undefined): TherapeuticStyleState {
  const questions = raw?.questions;
  return {
    questions: Array.isArray(questions) ? questions : [],
    answers: raw?.answers && typeof raw.answers === 'object' ? raw.answers : {},
    promptState: raw?.promptState ?? EMPTY_PROMPT_STATE,
  };
}

export async function getTherapeuticStyleState(): Promise<TherapeuticStyleState> {
  const raw = await beHttp.get<Partial<TherapeuticStyleState>>(BASE_PATH, undefined, {
    showErrorToast: false,
  });
  return normalize(raw);
}

export async function skipTherapeuticStylePrompt(): Promise<TherapeuticStylePromptState> {
  return beHttp.post<TherapeuticStylePromptState>(`${BASE_PATH}/skip`, undefined, {
    showErrorToast: false,
  });
}
